import { useEffect, useState } from "react";
import { toast } from "react-toastify";

import { OrgPaymentSetting, PaymentMethod } from "@graffer-inc/cloud-gov-core";
import { reportUnexpectedError } from "@graffer-inc/smart-apply";

import { useModal } from "../../../../../utils/custom-hooks";
import { default as tenantManagementClient } from "../../../../../application/apis/tenant-management/tenant-management-client";
import { CloudGovApiError } from "../../../../../application/domain/api-error";

type Props = {
  orgId: string;
};

export const useServiceProviderOrgPaymentSettingHooks = ({ orgId }: Props) => {
  const [isLoading, setIsLoading] = useState(false);

  const [paymentSetting, setPaymentSetting] = useState<
    OrgPaymentSetting | undefined
  >(undefined);

  const [merchantId, setMerchantId] = useState("");
  const [serviceId, setServiceId] = useState("");

  const [availablePaymentMethods, setAvailablePaymentMethods] = useState<
    PaymentMethod[]
  >(["credit_card"]);

  const [tdsAvailableAt, setTdsAvailableAt] = useState<Date | null>(null);

  const createPaymentSettingModal = useModal(false);
  const deletePaymentSettingModal = useModal(false);
  const paymentSettingMenuToggle = useModal(false);

  useEffect(() => {
    fetchPaymentSetting(orgId);
  }, []);

  useEffect(() => {
    if (paymentSetting) {
      setAvailablePaymentMethods(paymentSetting.availablePaymentMethods);
      setMerchantId(paymentSetting.sbpsMerchantId);
      setServiceId(paymentSetting.sbpsServiceId);
      setTdsAvailableAt(paymentSetting.tdsAvailableAt);
    }
  }, [paymentSetting]);

  const fetchPaymentSetting = async (id: string) => {
    setIsLoading(true);

    try {
      const { data } = await tenantManagementClient.fetchOrgPaymentSetting(id);
      setPaymentSetting(data);
    } catch (e) {
      if (CloudGovApiError.is404(e)) {
        setPaymentSetting(undefined);
      } else {
        reportUnexpectedError(e);
        toast.error("SBPS接続情報の読み込みに失敗しました。");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const onCreatePaymentSettingModalClose = () => {
    setMerchantId("");
    setServiceId("");
    createPaymentSettingModal.close();
  };

  const onSubmitPaymentSetting = async () => {
    try {
      await onUpsertPaymentSetting(
        orgId,
        merchantId,
        serviceId,
        availablePaymentMethods,
        tdsAvailableAt,
      );
      onCreatePaymentSettingModalClose();
    } catch (_) {
      // api errorのthrow時にonModalClose()を処理しない目的でtry-catchする
    }
  };

  const onUpsertPaymentSetting = async (
    id: string,
    merchantId: string,
    serviceId: string,
    availablePaymentMethods: PaymentMethod[],
    tdsAvailableAt: Date | null,
  ) => {
    setIsLoading(true);

    const setting: OrgPaymentSetting = {
      orgId: id,
      sbpsMerchantId: merchantId,
      sbpsServiceId: serviceId,
      availablePaymentMethods: availablePaymentMethods,
      tdsAvailableAt: tdsAvailableAt,
    };

    try {
      await tenantManagementClient.updateOrgPaymentSetting(setting);
      await fetchPaymentSetting(id);
      toast.success("SBPS接続情報の登録に成功しました。");
    } catch (e) {
      reportUnexpectedError(e);
      toast.error("SBPS接続情報の登録に失敗しました。");
      throw e;
    } finally {
      setIsLoading(false);
    }
  };

  const onDeletePaymentSetting = async (id: string) => {
    setIsLoading(true);

    try {
      await tenantManagementClient.deleteOrgPaymentSetting(id);
      await fetchPaymentSetting(id);
      toast.success("SBPS接続情報を削除しました。");
    } catch (e) {
      reportUnexpectedError(e);
      toast.error("SBPS接続情報の削除に失敗しました。");
      throw e;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    isLoading,
    merchantId,
    serviceId,
    paymentSetting,
    availablePaymentMethods,
    tdsAvailableAt,
    createPaymentSettingModal,
    deletePaymentSettingModal,
    paymentSettingMenuToggle,
    setMerchantId,
    setServiceId,
    setAvailablePaymentMethods,
    setTdsAvailableAt,
    onCreatePaymentSettingModalClose,
    onSubmitPaymentSetting,
    onDeletePaymentSetting,
  };
};
